/**
 * Helpers for split OTP digit inputs.
 * Android often pastes/autofills the full code via `onChange` (not `onPaste`),
 * and `maxLength={1}` blocks paste — callers should allow multi-digit values.
 */

export function applyOtpInputValue(
  previous: string[],
  index: number,
  rawValue: string,
  length: number,
): { next: string[]; focusIndex: number } {
  const digits = String(rawValue ?? '').replace(/\D/g, '');

  if (!digits) {
    const next = [...previous];
    next[index] = '';
    return { next, focusIndex: index };
  }

  // Single keypress, or overwrite of an existing digit (value becomes "56").
  if (digits.length === 1 || (digits.length === 2 && Boolean(previous[index]))) {
    const next = [...previous];
    next[index] = digits.slice(-1);
    return { next, focusIndex: index < length - 1 ? index + 1 : index };
  }

  // Multi-digit: paste or SMS/autofill (especially Android).
  const next = Array.from({ length }, (_, i) => previous[i] ?? '');
  if (digits.length >= length || index === 0) {
    for (let i = 0; i < length; i += 1) {
      next[i] = digits[i] ?? '';
    }
    return { next, focusIndex: Math.min(digits.length, length) - 1 };
  }

  for (let i = 0; i < digits.length && index + i < length; i += 1) {
    next[index + i] = digits[i] ?? '';
  }
  return {
    next,
    focusIndex: Math.min(index + digits.length, length) - 1,
  };
}

export function applyOtpPasteValue(clipboardText: string, length: number): string[] | null {
  const digits = String(clipboardText ?? '').replace(/\D/g, '').slice(0, length).split('');
  if (digits.length === 0) return null;
  return Array.from({ length }, (_, i) => digits[i] ?? '');
}

export function isOtpSubmitKey(key: string): boolean {
  return key === 'Enter' || key === 'Go' || key === 'Done';
}
